import type { ThemePreset } from "../types";
import {
  spacing,
  radius as forestRadius,
  shadows as tokenShadows,
  fontSize,
  lineHeight,
  fontWeight,
  display,
  container,
  widths,
  alpha,
  base,
  error,
  warning,
  success,
  info,
  misc,
  magenta,
  gray,
} from "../tokens";

// ─── Semantic Colors — resolve against imported primitives ────────────────────────────

// Colors object using imported primitives
const colors = {
  base,
  error,
  warning,
  success,
  info,
  brand: magenta,
  gray: gray,
};

// ─── Border Radius — minimal / sharp ────────────────────────────

const radius = {
  ...forestRadius,
  xxs: 1,
  xs: 2,
  sm: 3,
  md: 4,
  lg: 5,
  xl: 6,
  "2xl": 8,
  "3xl": 10,
  "4xl": 12,
};

// ─── Semantic Colors — Text ──────────────────────────────────────
// Same formulas as forest, resolved against alkemy+ primitives.

const text = {
  primary: gray[900],
  secondary: gray[700],
  secondaryHover: gray[800],
  tertiary: gray[600],
  tertiaryHover: gray[700],
  quaternary: gray[500],
  disabled: gray[500],
  placeholder: gray[500],
  placeholderSubtle: gray[300],
  white: base.white,
  brandPrimary: magenta[900],
  brandSecondary: magenta[700],
  brandTertiary: magenta[600],
  brandTertiaryAlt: magenta[600],
  primaryOnBrand: base.white,
  secondaryOnBrand: magenta[200],
  tertiaryOnBrand: magenta[200],
  quaternaryOnBrand: magenta[300],
  errorPrimary: error[600],
  warningPrimary: warning[600],
  successPrimary: success[600],
} as const;

// ─── Semantic Colors — Background ────────────────────────────────

const bg = {
  primary: gray[50],
  primaryAlt: gray[50],
  primaryHover: gray[100],
  primarySolid: gray[950],
  secondary: base.white,
  secondaryAlt: base.white,
  secondarySubtle: gray[25],
  secondaryHover: gray[50],
  secondarySolid: gray[600],
  tertiary: gray[100],
  quaternary: gray[200],
  active: gray[200],
  disabled: gray[100],
  disabledSubtle: gray[50],
  overlay: gray[950],
  brandPrimary: magenta[50],
  brandPrimaryAlt: magenta[50],
  brandSecondary: magenta[100],
  brandSolid: magenta[400],
  brandSolidHover: magenta[500],
  brandSection: magenta[800],
  brandSectionSubtle: magenta[700],
  errorPrimary: error[50],
  errorSecondary: error[100],
  errorSolid: error[600],
  warningPrimary: warning[50],
  warningSecondary: warning[100],
  warningSolid: warning[600],
  successPrimary: success[50],
  successSecondary: success[100],
  successSolid: success[600],
} as const;

// ─── Semantic Colors — Foreground ────────────────────────────────

const fg = {
  primary: gray[900],
  secondary: gray[700],
  secondaryHover: gray[800],
  tertiary: gray[600],
  tertiaryHover: gray[700],
  quaternary: gray[500],
  quaternaryHover: gray[600],
  quinary: gray[400],
  quinaryHover: gray[500],
  senary: gray[300],
  white: base.white,
  disabled: gray[400],
  disabledSubtle: gray[300],
  brandPrimary: magenta[400],
  brandPrimaryAlt: magenta[400],
  brandSecondary: magenta[400],
  errorPrimary: error[600],
  errorSecondary: error[500],
  warningPrimary: warning[600],
  warningSecondary: warning[500],
  successPrimary: success[600],
  successSecondary: success[500],
} as const;

// ─── Semantic Colors — Border ────────────────────────────────────

const border = {
  primary: gray[300],
  secondary: gray[200],
  tertiary: gray[100],
  disabled: gray[300],
  disabledSubtle: gray[200],
  brand: magenta[300],
  brandSolid: magenta[400],
  brandSolidAlt: magenta[400],
  error: error[300],
  errorSolid: error[600],
} as const;

// ─── Component Colors — Buttons ──────────────────────────────────

const buttonColors = {
  primary: {
    bg: magenta[400],
    bgHover: magenta[500],
    fg: base.white,
    fgHover: base.white,
    border: magenta[400],
    borderHover: magenta[500],
  },
  secondary: {
    bg: gray[900],
    bgHover: gray[950],
    fg: base.white,
    fgHover: base.white,
    border: gray[900],
    borderHover: gray[950],
  },
  secondaryColor: {
    bg: base.white,
    bgHover: magenta[50],
    fg: magenta[400],
    fgHover: magenta[500],
    border: magenta[300],
    borderHover: magenta[300],
  },
  tertiary: {
    fg: gray[600],
    fgHover: gray[700],
    bgHover: gray[50],
  },
  tertiaryColor: {
    fg: magenta[400],
    fgHover: magenta[500],
    bgHover: magenta[50],
  },
  primaryError: {
    bg: error[600],
    bgHover: error[700],
    fg: base.white,
    fgHover: base.white,
    border: error[600],
    borderHover: error[700],
  },
  secondaryError: {
    bg: base.white,
    bgHover: error[50],
    fg: error[700],
    fgHover: error[800],
    border: error[300],
    borderHover: error[300],
  },
  tertiaryError: {
    fg: error[700],
    fgHover: error[800],
    bgHover: error[50],
  },
} as const;

// ─── Component Colors — Avatars ──────────────────────────────────

const avatarColors = {
  bg: gray[100],
  contrastBorder: "#00000014",
  profilePhotoBorder: base.white,
  focusBorder: "#a0a0a624", // gray[400] + 14% alpha
} as const;

// ─── Component Colors — Breadcrumbs ──────────────────────────────

const breadcrumbColors = {
  fg: gray[600],
  fgHover: gray[700],
  bgHover: gray[50],
  brandBgHover: magenta[50],
  brandFgHover: magenta[700],
  iconFg: gray[500],
  iconFgHover: gray[700],
  brandIconFgHover: magenta[700],
} as const;

// ─── Component Colors — Icons ────────────────────────────────────

const iconColors = {
  fgBrand: magenta[400],
  fgBrandOnBrand: magenta[200],
  featuredModernBorder: gray[200],
  featuredLightFgBrand: magenta[400],
  featuredLightFgGray: gray[500],
  featuredLightFgError: error[600],
  featuredLightFgWarning: warning[600],
  featuredLightFgSuccess: success[600],
  featuredDarkFgBrand: base.white,
  featuredDarkFgGray: base.white,
  featuredDarkFgError: base.white,
  featuredDarkFgWarning: base.white,
  featuredDarkFgSuccess: base.white,
} as const;

// ─── Component Colors — Navigation ──────────────────────────────

const navColors = {
  itemIconFg: gray[500],
  itemIconFgActive: gray[700],
  itemButtonIconFg: gray[500],
  itemButtonIconFgActive: gray[700],
} as const;

// ─── Component Colors — Sliders ──────────────────────────────────

const sliderColors = {
  handleBorder: magenta[400],
  handleBg: base.white,
} as const;

// ─── Component Colors — Toggles ──────────────────────────────────

const toggleColors = {
  buttonFgDisabled: gray[50],
} as const;

// ─── Component Colors — Charts ───────────────────────────────────

const chartColors = {
  axisFg: gray[100],
  gridlineFg: gray[100],
  labelFg: gray[600],
  labelFgEmphasis: gray[700],
  bg: base.white,
  bgAlt: gray[50],
  tooltipBg: gray[950],
  tooltipFg: base.white,
  crosshairFg: gray[500],
  legendFg: text.tertiary,
  series: [
    { fg: misc.purple[500], fgHover: misc.purple[600], bg: misc.purple[100], bgHover: misc.purple[200] },
    { fg: misc.orangeDark[400], fgHover: misc.orangeDark[500], bg: misc.orangeDark[100], bgHover: misc.orangeDark[200] },
    { fg: misc.teal[300], fgHover: misc.teal[400], bg: misc.teal[50], bgHover: misc.teal[100] },
    { fg: warning[400], fgHover: warning[500], bg: warning[100], bgHover: warning[200] },
    { fg: misc.rose[400], fgHover: misc.rose[500], bg: misc.rose[100], bgHover: misc.rose[200] },
    { fg: misc.blueLight[400], fgHover: misc.blueLight[500], bg: misc.blueLight[100], bgHover: misc.blueLight[200] },
    { fg: misc.orange[300], fgHover: misc.orange[400], bg: misc.orange[100], bgHover: misc.orange[200] },
    { fg: misc.cyan[600], fgHover: misc.cyan[700], bg: misc.cyan[100], bgHover: misc.cyan[200] },
    { fg: misc.green[500], fgHover: misc.green[600], bg: misc.green[100], bgHover: misc.green[200] },
    { fg: misc.pink[400], fgHover: misc.pink[500], bg: misc.pink[100], bgHover: misc.pink[200] },
    { fg: misc.fuchsia[400], fgHover: misc.fuchsia[500], bg: misc.fuchsia[100], bgHover: misc.fuchsia[200] },
    { fg: misc.teal[600], fgHover: misc.teal[700], bg: misc.teal[100], bgHover: misc.teal[200] },
  ],
} as const;

// ─── Focus Rings ─────────────────────────────────────────────────
// magenta[400] = #FF78F3, gray[400] = #a0a0a6

const focusRings = {
  brand: "0px 0px 0px 4px #FF78F33d",
  brandShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #FF78F33d",
  brandShadowSm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f, 0px 0px 0px 4px #FF78F33d",
  gray: "0px 0px 0px 4px #a0a0a624",
  grayShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #a0a0a624",
  grayShadowSm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f, 0px 0px 0px 4px #a0a0a624",
  error: "0px 0px 0px 4px #f044383d",
  errorShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #f044383d",
} as const;

// ─── Preset ──────────────────────────────────────────────────────

export const forestInternalPreset: ThemePreset = {
  name: "forest-internal",
  defaultVariant: "light",
  variants: {
    light: {
      tokens: {
        base,
        error,
        warning,
        success,
        info,
        spacing,
        radius,
        shadows: tokenShadows,
        focusRings,
        fontFamily: '"Aeonik", sans-serif',
        fontFamilyMono: '"Aeonik Mono", monospace',
        fontSize,
        lineHeight,
        fontWeight,
        display,
        container,
        widths,
        text,
        bg,
        fg,
        border,
        buttonColors,
        avatarColors,
        breadcrumbColors,
        iconColors,
        navColors,
        sliderColors,
        toggleColors,
        chartColors,
        alpha,
      },
    },
  },
};
